import React from 'react';
import { GraduationCap, SearchIcon, X } from 'lucide-react';
import { Button, Input, Spinner } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { Course } from '@/services/apis/courses';
import { BaseTable, TableColumn } from '../../base';
import { BaseTableCell } from '../../base/BaseTable';
import CategorySelector from '../../base/selectors/CategorySelector';
import CourseActionsDropdown from './CourseActionsDropdown';

interface CoursesTabProps {
  courses: Course[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  error: string | null;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedCategoryId?: string;
  onCategoryChange: (value: string | undefined) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onEdit: (course: Course) => void;
  onDelete: (course: Course) => void;
  onPublish: (course: Course) => void;
  onUnpublish: (course: Course) => void;
  onAssignUsers: (course: Course) => void;
  onAssignDepartments: (course: Course) => void;
  isDeleting: boolean;
  isPublishing: boolean;
  isUnpublishing: boolean;
  loadMoreRef: React.RefObject<HTMLElement> | ((node: HTMLElement | null) => void);
  hasNextPage: boolean;
  onCreate: () => void;
}

const CoursesTab: React.FC<CoursesTabProps> = ({
  courses,
  isLoading,
  isFetchingNextPage,
  error,
  searchQuery,
  onSearchChange,
  selectedCategoryId,
  onCategoryChange,
  hasActiveFilters,
  onClearFilters,
  onEdit,
  onDelete,
  onPublish,
  onUnpublish,
  onAssignUsers,
  onAssignDepartments,
  isDeleting,
  isPublishing,
  isUnpublishing,
  loadMoreRef,
  hasNextPage,
  onCreate,
}) => {
  const { t } = useTranslation('admin');

  const courseColumns: TableColumn[] = [
    { name: t('courses.table.columns.name'), uid: 'name', sortable: true },
    { name: t('courses.table.columns.status'), uid: 'status', sortable: true },
    { name: t('courses.table.columns.createdBy'), uid: 'createdBy', sortable: true },
    { name: t('courses.table.columns.createdAt'), uid: 'createdAt', sortable: true },
    { name: t('courses.table.columns.actions'), uid: 'actions', sortable: false },
  ];

  const getCourseName = (course: Course) => {
    const englishTranslation = course.translations?.find((t) => t.language === 'en');
    return englishTranslation?.name || t('courses.placeholders.noName');
  };

  const getCourseDescription = (course: Course) => {
    const englishTranslation = course.translations?.find((t) => t.language === 'en');
    return englishTranslation?.description || '';
  };

  const renderCell = (course: Course, columnKey: string) => {
    switch (columnKey) {
      case 'name':
        return (
          <BaseTableCell
            text={getCourseName(course)}
            description={getCourseDescription(course)}
            icon={<GraduationCap className="w-5 h-5" />}
          />
        );

      case 'status':
        return (
          <BaseTableCell text={course.isPublished ? t('courses.status.published') : t('courses.status.unpublished')} />
        );

      case 'createdBy':
        const authorName = course.author
          ? `${course.author.firstName || ''} ${course.author.lastName || ''}`.trim() || course.author.email
          : t('courses.placeholders.notSpecified');
        return <BaseTableCell text={authorName} description={course.author?.email} />;

      case 'createdAt':
        return (
          <BaseTableCell
            text={
              course.createdAt
                ? new Date(course.createdAt).toLocaleDateString()
                : t('courses.placeholders.notSpecified')
            }
          />
        );

      case 'actions':
        return (
          <CourseActionsDropdown
            course={course}
            onEdit={onEdit}
            onDelete={onDelete}
            onPublish={onPublish}
            onUnpublish={onUnpublish}
            onAssignUsers={onAssignUsers}
            onAssignDepartments={onAssignDepartments}
            isPublishing={isPublishing}
            isUnpublishing={isUnpublishing}
            isDeleting={isDeleting}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('courses.title')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('courses.subtitle')}</p>
        </div>
        <Button color="primary" onPress={onCreate}>
          {t('courses.createButton')}
        </Button>
      </div>

      {/* Controls */}
      <div className="flex justify-between gap-3 items-end flex-wrap">
        <Input
          isClearable
          className="w-full sm:max-w-[44%]"
          placeholder={t('courses.search.placeholder')}
          startContent={<SearchIcon />}
          value={searchQuery}
          onClear={() => onSearchChange('')}
          onValueChange={onSearchChange}
        />
        <div className="flex gap-3 flex-wrap">
          <div className="w-[150px]">
            <CategorySelector
              value={selectedCategoryId || 'all'}
              onChange={(value) => onCategoryChange(value === 'all' ? undefined : (value as string))}
              placeholder={t('courses.table.columns.category')}
              variant="flat"
              label={t('courses.table.columns.category')}
              showAllOption={true}
            />
          </div>
          {hasActiveFilters && (
            <Button variant="flat" onPress={onClearFilters} startContent={<X className="w-4 h-4" />}>
              {t('courses.filters.clear')}
            </Button>
          )}
        </div>
      </div>

      {/* Courses Table */}
      <BaseTable
        data={courses}
        columns={courseColumns}
        isLoading={isLoading && courses.length === 0}
        loadingMessage={t('courses.table.loading')}
        error={error}
        emptyMessage={t('courses.table.empty.title')}
        emptyIcon={<GraduationCap className="w-12 h-12 text-gray-400" />}
        renderCell={renderCell}
        stickyHeader={true}
        hoverable={true}
        striped={true}
        idField="id"
      />

      {/* Infinite Scroll Trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef as React.RefObject<HTMLDivElement>} className="flex justify-center py-4">
          {isFetchingNextPage && <Spinner size="lg" />}
        </div>
      )}
    </div>
  );
};

export default CoursesTab;
